/*
 * Copyright(c) 2022 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as moment from 'moment';
import * as _ from 'underscore';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppConfigService } from '@app/common/app-config.service';
import { ModalConfig, SystemNotificationType, ModalRef } from '@core/public-api';
import { TranslateService } from '@ngx-translate/core';
import { appConstants } from '../../data-model/app-constants';
import { Subscription } from 'rxjs';
import { Logger, ModalService } from '@core/lib/services';
import { LookupService } from '../../services/lookup.service';
import { PatientContextService } from '../../services/patient-context.service';
import { UtilService } from '../../services/util.service';
import { ConfirmationActionComponent } from '../common/confirmation-action.component';
import { Authority, Confidentiality, PatientLocation, Gender, LookupData } from '../../data-model/lookup.types';
import { AssigningAuthority, PatientInfo, PatientUpdateDetails } from '../../data-model/patient.types';
import { AuditRecord_Patient } from '../../data-model/proxy.types';
import { UserPrivilegesService } from '@app/common/user-privileges.service';
import { NotifyService } from '../../../../../ng-ui/src/app/common/notify.service';

const log = new Logger('PatientHandlerComponent');

@Component({
  selector: 'app-patient-handler',
  templateUrl: './patient-handler.component.html',
  styleUrls: ['./patient-handler.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientHandlerComponent implements OnInit, OnDestroy {
  // Property
  private subscriptions: Array<Subscription> = [];
  patientHandlerForm: FormGroup;
  originalFormValue: any;

  loading: boolean;
  loadError: boolean;
  isErrorPopUp = false;

  dob: moment.Moment;
  todayEod: moment.Moment = moment().endOf('day');

  domainList: Authority[] = [];
  genderList: Gender[] = [];
  confidentialities: Confidentiality[] = [];
  patientLocationList: PatientLocation[] = [];

  patientDetails: PatientInfo;
  patientUpdateDetails: PatientUpdateDetails;
  originalIdentifier: AssigningAuthority[] = [];
  dateFormat: string;
  dateDisplayFormat: string;
  dateFormatDelimiter: string;

  private auditComments = [];

  patientDataInfoBasedonDomain = [];
  isMRNNumberEditable: boolean;
  dobInEditing = false;
  confirmationMessage: string;

  // Worklist notification object.
  notifications = {
    a_errorGeneral: {
      id: 'errorGeneral',
      label: this.translate.instant('msg.error.general'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    b_errorLoading: {
      id: 'errorLoading',
      label: this.translate.instant('msg.patient.error.loading'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    c_successUpdate: {
      id: 'successPatientUpdated',
      label: this.translate.instant('msg.patient.success.patientUpdated'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.success,
      multiLineLayout: false
    },
    d_errorUpdate: {
      id: 'errorPatientUpdate',
      label: this.translate.instant('msg.patient.error.patientUpdated'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    },
    e_warningTimeout: {
      id: 'warningPatientUpdateTimeout',
      label: this.translate.instant('msg.patient.error.patientUpdated.exceeded.time'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.moderateSeverityPrimary,
      multiLineLayout: false
    },
    f_patientIdExist: {
      id: 'errorPatientIdExist',
      label: this.translate.instant('msg.patientId.error.already.exists'),
      show: false,
      hideClose: true,
      type: SystemNotificationType.highSeverityPrimary,
      multiLineLayout: false
    }
  };

  // Constructor
  constructor(
    public config: ModalConfig,
    private modalRef: ModalRef,
    private cd: ChangeDetectorRef,
    private translate: TranslateService,
    private fb: FormBuilder,
    private appConfigService: AppConfigService,
    private lookupService: LookupService,
    private patientContextService: PatientContextService,
    private modalService: ModalService,
    private utilService: UtilService,
    private userPrivilegeService: UserPrivilegesService,
    private notify: NotifyService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadError = false;
    this.notifications.a_errorGeneral.show = false;
    this.notifications.b_errorLoading.show = false;
    this.notifications.c_successUpdate.show = false;
    this.notifications.d_errorUpdate.show = false;
    this.notifications.f_patientIdExist.show = false;

    this.config.headerContent = this.translate.instant('label.editPatient');
    this.setDateFormat();
    this.subscribeDateFormat();
    this.cd.detectChanges();

    this.buildForm();
    this.subscribeChange();
  }

  ngOnDestroy(): void {
    this.patientContextService.setPatientData({});
    this.subscriptions.forEach(subscriber => {
      subscriber.unsubscribe();
    });
  }

  // Form
  private buildForm() {
    this.patientHandlerForm = this.fb.group({
      patientName: [
        '',
        [
          Validators.required,
          this.utilService.validatorForRegex(appConstants.regEx.specialChars),
          this.utilService.validatorForRegex(appConstants.regEx.beginBlankOrDigit)
        ]
      ],
      iPatName: ['', [this.utilService.validatorForRegex(appConstants.regEx.specialChars)]],
      gender: [''],
      dob: ['', [Validators.required]],
      location: [''],
      domain: [''],
      internalPatientId: [''],
      confidentialPatient: [''],
      domainPatientId: [
        '',
        [
          Validators.required,
          Validators.pattern(appConstants.regEx.space),
          this.utilService.validatorForRegex(appConstants.regEx.specialCharsForPatientId)
        ]
      ]
    });
  }

  get formControls() {
    return this.patientHandlerForm.controls;
  }

  subscribeDateFormat() {
    this.subscriptions.push(
      this.appConfigService.getDateTimeObserver().subscribe(() => {
        this.setDateFormat();
      })
    );
  }

  private setDateFormat() {
    const dateTimePref = this.appConfigService.getDateTimePref();
    if (dateTimePref) {
      this.dateFormat = dateTimePref.inputFormat;
      this.dateDisplayFormat = dateTimePref.dateFormat;
      this.dateFormatDelimiter = dateTimePref.delimiter;
    }
  }

  //set user selection to form fields
  setUserSelection(field: string, selectedItem: any) {
    this.clearAllNotification();
    this.resetDateInEditingFlags();
    this.patientHandlerForm.get(field).setValue(selectedItem);
    if (field === 'domain') {
      const patId = _.find(
        this.patientDataInfoBasedonDomain,
        el => el.internalAssigningAuthorityId == selectedItem.internalAssigningAuthorityId
      )?.patientId;
      this.patientHandlerForm.get('domainPatientId').patchValue(patId);
    }
    if (this.originalFormValue) {
      if (JSON.stringify(this.originalFormValue) === JSON.stringify(this.patientHandlerForm.value)) {
        this.patientHandlerForm.markAsPristine();
      } else {
        this.patientHandlerForm.markAsDirty();
      }
    }
    this.cd.detectChanges();
  }

  //remove all the notification banner from Edit exam modal
  clearAllNotification() {
    this.notifications.a_errorGeneral.show = false;
    this.notifications.b_errorLoading.show = false;
    this.notifications.c_successUpdate.show = false;
    this.notifications.d_errorUpdate.show = false;
    this.notifications.e_warningTimeout.show = false;
    this.notifications.f_patientIdExist.show = false;
  }

  /**
   * Reset the form to original values
   */
  resetForm() {
    this.clearAllNotification();
    this.patientHandlerForm.patchValue(this.originalFormValue);
    this.patientHandlerForm.markAsPristine();
    this.patientDataInfoBasedonDomain = JSON.parse(JSON.stringify(this.originalIdentifier));
    this.cd.detectChanges();
  }

  // Populate dropdown list
  private populateList(lookUpData) {
    log.debug('RM Patient Component populateList - Invoked', lookUpData);
    const lookupFields = appConstants.lookup;
    Object.keys(lookUpData).forEach(field => {
      switch (field) {
        case lookupFields.AUTHORITIES:
          this.domainList = lookUpData[field];
          break;
        case lookupFields.PATIENT_LOCATIONS:
          this.patientLocationList = lookUpData[field];
          break;
        case lookupFields.GENDER:
          this.genderList = lookUpData[field];
          break;
        case lookupFields.CONFIDENTIALITY:
          this.confidentialities = lookUpData[field];
          break;
        default:
          break;
      }
    });
    log.debug('RM Patient Component populateList - Loaded');
  }

  onInput() {
    this.isErrorPopUp = false;
    this.clearAllNotification();
  }

  // Set data into form & data modal used for updating
  private setPatientData(patientData: PatientInfo) {
    log.debug('RM Patient Component setPatientData - Invoked', patientData);
    this.isMRNNumberEditable = this.userPrivilegeService.getUserPrivileges().indexOf('PrivilegedModifyPatient') > -1;
    try {
      const infoFields = appConstants.patientInfo;
      if (patientData && Object.keys(patientData).length) {
        this.patientDetails = { ...patientData };
        this.setDomainList(patientData.identifier);
        Object.keys(patientData).forEach(field => {
          if (patientData[field] != null) {
            switch (field) {
              case infoFields.DOB:
                const dob = moment(patientData.dob, appConstants.format.dob);
                this.patientHandlerForm.get('dob').patchValue(dob);
                break;
              case infoFields.PATIENT_ID:
                this.patientHandlerForm.get('domainPatientId').patchValue(patientData.identifier[0].patientId);
                break;
              case infoFields.NAME:
                this.patientHandlerForm.get('patientName').patchValue(patientData.patientName);
                break;
              case infoFields.I_NAME:
                this.patientHandlerForm.get('iPatName').patchValue(patientData.iPatName);
                break;
              case infoFields.AUTHORITY_CKEY:
                break;
              case infoFields.GENDER:
                const gender = this.lookupService.findGenderById(patientData.gender);
                this.patientHandlerForm.get('gender').patchValue(gender);
                break;
              case infoFields.PATIENT_FLAG:
                const confidentialPatient = this.lookupService.findConfidentialityById(patientData.confidentialPatient);
                this.patientHandlerForm.get('confidentialPatient').patchValue(confidentialPatient);
                break;
              case infoFields.LOCATION_NAME:
                const location = this.lookupService.findpatientLocationsByName(patientData.locationName);
                this.patientHandlerForm.get('location').patchValue(location);
                break;
              case infoFields.IDENTIFIER:
                const authority = this.lookupService.findAuthoritiesById(
                  patientData.identifier[0].internalAssigningAuthorityId
                );
                this.patientHandlerForm.get('domain').patchValue(authority);
                break;
              default:
                break;
            }
            this.originalFormValue = { ...this.patientHandlerForm.value };
          }
        });
      }
    } catch (ex) {
      this.loadingFailed(ex);
    }

    this.loading = false;
    this.patientHandlerForm.markAsPristine();
    this.cd.detectChanges();
    log.debug('RM Patient Component setPatientData - loaded');
  }

  private setDomainList(identifier: AssigningAuthority[]) {
    let patientIdIndex = 0;
    let domainListSliced = this.domainList;
    const patientIdSize: number[] = [];
    this.domainList.length === identifier.length &&
      this.domainList.forEach((domainListElement, index) =>
        identifier.some(patientDataElement => {
          if (
            domainListElement.internalAssigningAuthorityId === patientDataElement.internalAssigningAuthorityId &&
            patientDataElement.patientId == null
          ) {
            patientIdSize[patientIdIndex] = index;
            patientIdIndex++;
          }
        })
      );

    let indexPatientId = 0;
    if (patientIdIndex !== 0) {
      domainListSliced = domainListSliced.reduce((domainListFiltered, value, index) => {
        if (!patientIdSize.includes(index)) {
          domainListFiltered.push(value);
        }
        return domainListFiltered;
      }, []);
      this.domainList = domainListSliced;
      indexPatientId = identifier.findIndex(
        a => a.internalAssigningAuthorityId === this.domainList[0].internalAssigningAuthorityId
      );
    } else {
      domainListSliced = domainListSliced.reduce((domainListFiltered, value, index) => {
        indexPatientId = identifier.findIndex(
          a => a.internalAssigningAuthorityId === this.domainList[index].internalAssigningAuthorityId
        );
        if (indexPatientId >= 0) {
          domainListFiltered.push(value);
        }
        return domainListFiltered;
      }, []);
      this.domainList = domainListSliced;
    }
  }

  // Set form Data
  private loadFormData() {
    this.notifications.b_errorLoading.show = false;
    if (this.config?.data?.patientId) {
      this.patientContextService
        .getPatientRecord(this.config.data.patientId)
        .then(patientInfo => {
          this.setPatientData(patientInfo);
          this.patientDataInfoBasedonDomain = [...patientInfo.identifier];
          this.originalIdentifier = JSON.parse(JSON.stringify(patientInfo.identifier));
        })
        .catch(ex => {
          this.loadingFailed(ex);
        });
    } else {
      this.loadingFailed();
    }
  }

  // Subcribe to observers
  private subscribeChange() {
    this.subscriptions.push(
      this.lookupService.getLookUpData().subscribe((data: LookupData) => {
        if (data.error) {
          this.loadingFailed(data.error);
        } else {
          this.populateList(data);
          this.loadFormData();
        }
      })
    );
  }

  // Handler - Loading failed
  private loadingFailed(err?) {
    this.loading = false;
    this.loadError = true;
    this.notifications.b_errorLoading.show = true;
    this.cd.detectChanges();
    log.debug('RM Patient Component loading failed', err);
  }

  // Cancel Update action
  closeModal() {
    this.modalRef.close();
  }

  // Function to initiate the update request
  updatePatient() {
    try {
      this.notifications.d_errorUpdate.show = false;
      this.notifications.c_successUpdate.show = false;
      const finalIdentifierObj = <AssigningAuthority>{};
      const identifierOldList = this.patientDetails.identifier;
      const identifierOldListCopy = JSON.parse(
        JSON.stringify(this.patientDetails.identifier)
      ) as typeof identifierOldList;
      this.patientDetails.identifier.forEach(data => {
        if (
          data.internalAssigningAuthorityId === this.patientHandlerForm.get('domain').value.internalAssigningAuthorityId
        ) {
          data.patientId = this.patientHandlerForm.get('domainPatientId').value;
        }
      });

      identifierOldListCopy.forEach((element, index) => {
        this.patientDetails.identifier.forEach(patientDetailsIdentifier => {
          if (
            patientDetailsIdentifier.internalAssigningAuthorityId === element.internalAssigningAuthorityId &&
            patientDetailsIdentifier.patientId !== element.patientId
          ) {
            Object.assign(finalIdentifierObj, this.patientDetails.identifier[index]);
          } else {
            Object.assign(finalIdentifierObj, null);
          }
        });
      });

      const patientDetailsUpdate: PatientUpdateDetails = {
        internalPatientId: this.patientDetails.internalPatientId,
        confidentialPatient: this.patientHandlerForm.get('confidentialPatient').value.code,
        patientName: this.patientHandlerForm.get('patientName').value,
        iPatName: this.patientHandlerForm.get('iPatName').value,
        pPatName: this.patientDetails.pPatName,
        gender: this.patientHandlerForm.get('gender').value.code,
        dob: moment(this.patientHandlerForm.get('dob').value).format(appConstants.format.dbDate),
        locationName: this.patientHandlerForm.get('location').value.locationDesc,
        lastUpdatedDateTime: this.patientDetails.lastUpdatedDateTime,
        userName: this.appConfigService.getUserId(),
        identifier: finalIdentifierObj
      };

      // Show confirmation modal
      if (this.appConfigService.getAppConfigByKey('ISF.StudyManagement.AllowCreateRISOrderInfo') == 'true') {
        this.confirmationMessage = this.translate.instant('modal.patient.confirmation.patientUpdate.text');
      } else {
        this.confirmationMessage = this.translate.instant('modal.patient.risConfirmation.patientUpdate.text');
      }
      this.modalService.openModal(ConfirmationActionComponent, {
        isBlockModal: true,
        modalClass: 'modal--medium',
        data: {
          headerNotificationText: 'modal.patient.confirmation.patientUpdate',
          headerNotificationType: SystemNotificationType.highSeverityPrimary,
          message: this.confirmationMessage,
          noButtonLabel: this.translate.instant('button.cancel'),
          yesButtonLabel: this.translate.instant('button.submit'),
          callBack: () => this.startPatientRecord(this.generateAuditInfo(patientDetailsUpdate), patientDetailsUpdate)
        }
      });
    } catch (ex) {
      log.error('RM Patient Component updatePatient - Exception', ex);
      this.notifications.d_errorUpdate.show = true;
      this.cd.detectChanges();
    }
  }

  // Function to generate audit information
  private generateAuditInfo(patientDetailsUpdate: PatientUpdateDetails): AuditRecord_Patient {
    const patientLabel = appConstants.patientInfo;

    this.auditComments.push(appConstants.auditLabel.commentHead);
    Object.keys(patientDetailsUpdate).forEach(field => {
      if (field === patientLabel.PATIENT_FLAG) this.appendComments(patientLabel.LABEL_PATIENT_FLAG, field, 'desc');
      if (field === patientLabel.NAME) this.appendComments(patientLabel.LABEL_NAME, field, undefined);
      if (field === patientLabel.GENDER) this.appendComments(patientLabel.LABEL_GENDER, field, 'desc');
      if (field === patientLabel.DOB) this.appendComments(patientLabel.DATE_OF_BIRTH, field, undefined, true);
      if (field === patientLabel.I_NAME) this.appendComments(patientLabel.LABEL_I_NAME, field, undefined);
      if (field === patientLabel.LOCATION_NAME)
        this.appendComments(patientLabel.LABEL_LOCATION_NAME, 'location', 'locationDesc');
    });

    return {
      actionType: patientLabel.ACTION_TYPE,
      patientIdentifier: patientDetailsUpdate.internalPatientId.toString(),
      patientCkey: patientDetailsUpdate.internalPatientId,
      examCkey: this.config.data.examId,
      comments: this.auditComments.join('')
    };
  }
  private appendComments(fieldLabel: string, field: string, target: string, usePayloadInfo = false): void {
    let fromData: string, toData: string;
    if (usePayloadInfo) {
      fromData = this.patientDetails[field];
      toData = this.patientHandlerForm.get(field).value.toString();
    } else {
      fromData =
        target && this.originalFormValue[field] ? this.originalFormValue[field][target] : this.originalFormValue[field];
      toData =
        target && this.patientHandlerForm.get(field).value
          ? this.patientHandlerForm.get(field).value[target]
          : this.patientHandlerForm.get(field).value;
    }
    if (fromData != toData) {
      this.auditComments.push(
        [
          fieldLabel,
          appConstants.auditLabel.from,
          fromData,
          appConstants.auditLabel.to,
          toData,
          appConstants.auditLabel.end
        ].join('')
      );
    }
  }

  // Function to trigger the updatePatientRecord
  startPatientRecord(auditRecord: AuditRecord_Patient, patientDetailsUpdate: PatientUpdateDetails): Promise<void> {
    return this.patientContextService
      .updatePatientRecord(patientDetailsUpdate, auditRecord)
      .then(() => {
        log.debug('RM Patient Component startPatientRecord Patient Sucess');
        this.notifications.c_successUpdate.show = true;
        this.loading = true;
        this.cd.detectChanges();
        this.loadFormData();
      })
      .catch(ex => {
        log.error('RM Patient Component startPatientRecord Patient failed', ex);
        if (ex.status == appConstants.appURLs.timeoutStatus) {
          this.notifications.e_warningTimeout.show = true;
          this.patientHandlerForm.disable();
        } else if (ex.status == appConstants.appURLs.unprocessableEntity) {
          this.isErrorPopUp = true;
          this.notifications.f_patientIdExist.show = true;
        } else if (ex.status === 501) {
          this.notify.critical(
            '<h3>' +
              this.translate.instant('studyManagement.purgeFailureTitle') +
              '</h3><div>' +
              this.translate.instant('studyManagement.patientPurgeFailureMessage') +
              '</div><br/>',
            this.translate.instant('editPatient.label')
          );
        } else if (ex.status == appConstants.appURLs.partialStatus) {
          this.updatePartialFailureHandler();
        } else {
          this.notifications.d_errorUpdate.show = true;
        }
        this.cd.detectChanges();
        if (ex.status == appConstants.appURLs.conflictStatus) {
          this.updateFailureHandler_oldData();
        }
      });
  }

  // Checking the Date values to show Date required error
  dobDateBoxvalueChanged() {
    const value = this.patientHandlerForm.get('dob').value;
    this.dobInEditing = value === undefined ? true : false;
  }

  // Reseting the Date Editing flags
  resetDateInEditingFlags() {
    this.dobInEditing = false;
  }

  // Function to handle old patient data update
  private updateFailureHandler_oldData() {
    const sub = this.modalService
      .openModal(this.translate.instant('modal.patient.error.conflict.patientUpdate.text'), {
        headerContent: this.translate.instant('modal.patient.error.conflict.patientUpdate'),
        disableHeaderCloseBtn: true,
        enableYesBtn: false,
        isDecisionModal: false,
        noButtonLabel: this.translate.instant('button.ok')
      })
      .afterClosed.subscribe(() => {
        sub.unsubscribe();
        this.ngOnInit();
      });
  }

  // Function to handle partial patient update
  private updatePartialFailureHandler() {
    this.notifications.d_errorUpdate.show = true;
    this.cd.detectChanges();
    const sub = this.modalService
      .openModal(this.translate.instant('msg.error.partialUpdate'), {
        headerContent: this.translate.instant('msg.record.management.text'),
        disableHeaderCloseBtn: true,
        enableYesBtn: false,
        isDecisionModal: false,
        noButtonLabel: this.translate.instant('button.ok')
      })
      .afterClosed.subscribe(() => {
        sub.unsubscribe();
        this.ngOnInit();
      });
  }
}
